/*
	IndexedDB storage API
	uses https://github.com/jakearchibald/idb to operate with indexedDb
	implements the persistent storage for application
 */

import {elog} from "@/engine/helpers/logging.js";
import * as idb from 'idb';

class IDB {
	constructor(dbname) {
		this.db = null;
		this.getDbConnection(dbname);
		//this.getDbConnectionLowlevel(dbname);
	}

	async getDbConnection(dbname) {
		const IDB_VERSION = 3;
		try {
			this.db = await idb.openDB(dbname, IDB_VERSION, {
                upgrade(db, oldVersion, newVersion) {
                    if (oldVersion > 0) {
						db.deleteObjectStore('messages');
                    }
					let store = db.createObjectStore('messages', {keyPath: 'uid'});
					store.createIndex('timestamp', 'timestamp');
					elog("DEBUG", !oldVersion ? "New 'messages' storage has been created" : `Messages storage has been upgraded from version ${oldVersion} to ${IDB_VERSION}`);

					db.createObjectStore('context', {keyPath: 'context'})
                },
				blocked(currentVersion, blockedVersion, event) {
					elog("DEBUG",  "idb blocked" );
				},
				blocking(currentVersion, blockedVersion, event) {
					elog("DEBUG",  "idb blocking" );
				},
				terminated() {
					elog("DEBUG",  "idb terminated" );
				},
			});
			//console.log("Storage has been opened");
		} catch(e) {
			elog("Error: IndexedDB wasn't opened: " + e);
		}
	}

	//lowlevel solution
	getDbConnectionLowlevel(dbname) {

		if (this.db) {
			return;
		}
		const IDB_VERSION = 3;
		this.indexedDBOpenRequest = indexedDB.open(dbname, IDB_VERSION);

		// This top-level error handler will be invoked any time there's an IndexedDB-related error.
		this.indexedDBOpenRequest.onerror = function(error) {
			elog("WARN", 'IndexedDB opening error:', error.message);
		};

		// This should only execute if there's a need to create a new database for the given IDB_VERSION.
		this.indexedDBOpenRequest.onupgradeneeded = function(event) {
			let db = event.target.result;
			if (db.version > 0) {
				db.deleteObjectStore('messages');
			}
			let store = db.createObjectStore('messages', {keyPath: 'uid'});
			store.createIndex('timestamp', 'timestamp');
			elog("DEBUG", !db.version ? "New 'messages' storage has been created" : `Messages storage has been upgraded from version ${db.version} to ${IDB_VERSION}`);

			db.createObjectStore('context', {keyPath: 'context'})
		};

		this.indexedDBOpenRequest.onsuccess = function(event) {
			this.db = idb.wrap(event.target.result);
		}.bind(this);

		this.indexedDBOpenRequest.onblocked = function(event) {
			elog("DEBUG", "idb blocked");
		}
	}


	async storeContext(name, data) {
		let db = await this.Db();
		if (!db)
			throw new Error("storage isn't ready");

		data.context = name
		db.put("context", data)
	}

	async getContext(name) {
		let db = await this.Db();
		if (!db)
			throw new Error("storage isn't ready");
		return await db.get("context", name);
	}

	/*
	message = {
		uuid: if null - will be set to a new value
		timestamp: timestamp or null, if null - will be set to current timestamp
		type: message_type
		payload: depend of type field
	}
 */
	async storeMessage(message) {

		let db = await this.Db();
		if (!db)
			throw new Error("storage isn't ready");

		if (! message.timestamp)
			message.timestamp = new Date().getTime();

		if (! message.uid )
			message.uid = this.uuidv4();

		await db.add('messages', message);

		return message;
	}

	async getMessagesFrom(idx, n) {
		let db = await this.Db();
		if (!db)
			throw new Error("storage isn't ready");

		let messages = [];
		let counter = 0;
		const tx = db.transaction('messages', "readonly");
		for await (const cursor of tx.store.index('timestamp').iterate(IDBKeyRange.upperBound(idx), "prev")) {
			messages.push({ ...cursor.value });
			counter++;
			if (counter === n)
				break
		}
		await tx.done;
		return messages;
	}

	/* wait to db became ready */
	async Db() {
		if (this.db)
			return this.db;
		let self = this;
		let maxWaiting = 3000; //ms
		let step = 200; //ms
		let startedAt = new Date().getTime();
		let iterator = function (resolve, reject) {
			if ((new Date().getTime() - startedAt) > maxWaiting)
				return reject();
			if (self.db)
				return resolve();
			setTimeout(iterator, step, resolve, reject);
		};
		try {
			await new Promise((resolve, reject) => setTimeout(iterator, step, resolve, reject));
			return self.db
		} catch {
			elog("DEBUG", "Storage waiting timeout " + (new Date().getTime() - startedAt) + "ms");
		}
		return null;
	}

	//simple uid generator, it's enough to our purpose
	uuidv4() {
		return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
			var r = Math.random() * 16 | 0, v = c == 'x' ? r : (r & 0x3 | 0x8);
			return v.toString(16);
		});
	}


	/*
	//more advanced uuid gerenator, with using crypto API
	function uuidv4() {
		return ([1e7]+-1e3+-4e3+-8e3+-1e11).replace(/[018]/g, c =>
			(c ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> c / 4).toString(16)
		);
	}

	 */
}

export {IDB};
